import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { AddRecordedVoiceoverSceneGroupTool } from 'js/src/agent/tools/AddRecordedVoiceoverSceneGroupTool';
import { AddTalkingHeadSceneGroupTool } from 'js/src/agent/tools/AddTalkingHeadSceneGroupTool';
import { AddVoiceoverSceneTool } from 'js/src/agent/tools/AddVoiceoverSceneTool';
import { AskUserQuestionTool } from 'js/src/agent/tools/AskUserQuestionTool';
import { FindWaveformSceneDesignTool } from 'js/src/agent/tools/FindWaveformSceneDesignTool';
import { ImageAskTool } from 'js/src/agent/tools/ImageAskTool';
import { ListSceneDesignTool } from 'js/src/agent/tools/ListSceneDesignTool';
import { SetAspectRatioTool } from 'js/src/agent/tools/SetAspectRatioTool';
import { SetCaptionsTool } from 'js/src/agent/tools/SetCaptionsTool';
import { SetWaveformStyleTool } from 'js/src/agent/tools/SetWaveformStyleTool';
import { SplitSceneGroupTool } from 'js/src/agent/tools/SplitSceneGroupTool';

export const createPodcastWaveformVideoSkill: Skill = {
    name: SKILL_NAMES.createPodcastWaveformVideo,
    description: 'Uploaded voiceover/podcast recording turned into an audiogram/waveform video',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        title: 'Audiogram',
        blurb: 'Turn a voiceover or podcast into a waveform video.',
        icon: 'graphic_eq',
        gradient: ['#4A7C77', '#6BA3AF'],
    },
    get instructions(): string {
        return `# Create Podcast Waveform Video (Audiogram)

For turning an uploaded recording — a podcast segment, interview clip, or narration —
into an audiogram: a video where the audio is the star, with an animated waveform
pulsing to the voice and animated captions. The Headliner style.

## Setup
- Ask which aspect ratio the user wants with ${AskUserQuestionTool.toolName}
  (type "aspect_ratio"), unless they already told you. Apply it with
  ${SetAspectRatioTool.toolName} BEFORE
  building any scenes — designs are laid out per aspect ratio, so changing it later
  reworks the whole video.
- Add the recording with ${AddRecordedVoiceoverSceneGroupTool.toolName} — a single scene group
  for the whole recording. Transcription and word timings happen automatically.
  That tool only accepts audio: if the user attached a *video* (e.g. a video
  podcast), that's a talking-head build (${AddTalkingHeadSceneGroupTool.toolName}), not an
  audiogram, and this skill doesn't apply.

## The waveform: ONE overlay spanning the whole video
1. Call ${FindWaveformSceneDesignTool.toolName} to get the audiogram design's id. If it reports
   the template has no waveform design, tell the user this template doesn't support
   the audiogram style.
2. Call ${AddVoiceoverSceneTool.toolName} with that design id and cover_entire_video: true (no words).
3. Call ${SetWaveformStyleTool.toolName} with the new overlay's scene index and no waveform_name —
   always, right after placing the overlay. The picker IS the question: don't ask
   permission first ("want to see the options?") and don't use ${AskUserQuestionTool.toolName} for
   it. Only pass waveform_name when the user has already named a style.

Never place the waveform with word-bounded overlays. They get re-timed to transcript
word boundaries, which leaves the leading silence — including the video's very first
frame and thumbnail — showing the template's plain default waveform base: a static
bar graphic that doesn't react to the audio. The full-video overlay owns everything
from frame 0 to the end.

## Captions
The audiogram design includes animated captions synced to the speech — you get them
for free. Do NOT also enable scene-group captions with ${SetCaptionsTool.toolName}; that would
double up the text.

## Structure
With the full-video waveform overlay in place, the video is already a complete
audiogram. Add structure only where it earns its place, and always AFTER the
waveform overlay (later overlays carve their span out of it; the waveform keeps
everything else):
- An intro overlay over the first few words for episode title, show name, or
  speaker name(s) — ${ListSceneDesignTool.toolName} with narrative_thread="voiceover" and pick a
  title/intro design.
- An outro overlay (logo, "listen to the full episode", handle/URL) over the
  closing words.
- Media/b-roll overlays in the middle only if the user asks or the content clearly
  demands a visual — the waveform shows through everywhere you don't add one.
- No text assets that duplicate the captions.

## Limits and defaults
- There is no tool to cut sections out of the recording, so you cannot trim to
  highlights. If asked, say so plainly and request a pre-trimmed upload. Do NOT
  fake a trim with ${SplitSceneGroupTool.toolName} or scene deletion — scenes can't be deleted
  from voice-driven groups.
- Skip background music unless the user explicitly asks — music under spoken
  podcast audio muddies it.

## Finishing checks
On top of the standard review, ${ImageAskTool.toolName} the waveform scene and any intro/outro
overlays: the waveform is clearly visible, captions are readable and don't collide
with the waveform or other text, and the FIRST frame of the video shows the
audiogram design — not a plain static waveform placeholder.`;
    },
};
